import {
  InterpolateInplaceAction,
  MouseDownEvent,
  MouseMoveEvent,
  MouseWheelEvent,
  PIXEL_RATIO,
  ProgressBar,
  SequenceAction,
  throttle,
  Vector2,
} from '@cgs/wge';

export class ScrollableProgressBarComponent {
  private lastSavedPosition: Vector2 = new Vector2(0, 0);
  private isScrollable: boolean = true;
  private sensitivity: number = 0.00005;
  protected acceleration: number = 0.5;

  constructor(protected readonly progressBar: ProgressBar) {}

  public initialize(): void {
    const throttledSavePosition = throttle(
      (event: MouseMoveEvent) => this.handleSavePosition(event),
      50
    );
    const throttledMouseWheel = throttle(
      (event: MouseWheelEvent, scene: ProgressBar) => this.handleMouseWheel(event, scene),
      25
    );

    this.progressBar.eventReceived.listen((event) => {
      if (!this.isScrollable) {
        return;
      }
      if (event instanceof MouseWheelEvent) {
        event.mouse.originalEvent?.preventDefault();

        throttledMouseWheel(event, this.progressBar);
      }
      if (event instanceof MouseDownEvent) {
        this.lastSavedPosition = event.event.location.clone();
      }
      if (event instanceof MouseMoveEvent) {
        event.mouse.originalEvent?.preventDefault();

        throttledSavePosition(event);
        requestAnimationFrame(() => {
          this.handleSwipe(event, this.progressBar);
        });
      }
    });
  }

  public set scrollableSize(size: Vector2) {
    const progressAnimationKeys = Object.keys(this.progressBar.animations || {});
    this.isScrollable = size.y !== 0;
    this.sensitivity = this.acceleration / -size.y;

    progressAnimationKeys.forEach((key) => {
      const animation = this.progressBar.animations?.[key] as SequenceAction;
      if (!animation.childActions) {
        return;
      }
      animation.childActions.forEach((action) => {
        if (action instanceof InterpolateInplaceAction) {
          action.withValues(action.startValue.clone(), size);
        }
      });
    });
  }

  public get scene(): ProgressBar {
    return this.progressBar;
  }

  public resetProgress(): void {
    this.progressBar.progress = 0;
  }

  private handleSavePosition(event: MouseMoveEvent): void {
    this.lastSavedPosition = event.moveEvent.location.clone();
  }

  private handleMouseWheel(event: MouseWheelEvent, scene: ProgressBar): void {
    const progressDelta = event.wheelDelta * this.sensitivity;

    scene.progress = Math.max(0, Math.min(1, scene.progress + progressDelta));
  }

  private handleSwipe(event: MouseMoveEvent, scene: ProgressBar): void {
    const delta = this.lastSavedPosition.clone().subtract(event.moveEvent.location);

    const sensitivity = this.sensitivity;

    const progressDelta = delta.y * sensitivity * PIXEL_RATIO;

    scene.progress = Math.max(0, Math.min(1, scene.progress + progressDelta));
  }
}
